import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";

/**
 * Reusable confirmation dialog styled with the app's glass design system.
 * Replaces native window.confirm for a product-level experience.
 */
const ConfirmDialog = ({
  open,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger", // "danger" | "default"
  loading = false,
  onConfirm,
  onCancel,
  darkMode = true,
}) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onCancel]);

  const confirmClasses =
    tone === "danger"
      ? "bg-red-500 hover:bg-red-400 active:bg-red-600 text-white shadow-glow-sm"
      : "bg-accent-gradient hover:brightness-110 active:brightness-95 text-[#04121F] shadow-glow-sm";

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="confirm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-ink-950/80 p-4 backdrop-blur-sm"
          onClick={() => !loading && onCancel?.()}
        >
          <motion.div
            key="confirm-panel"
            role="alertdialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-sm overflow-hidden rounded-3xl shadow-2xl ${
              darkMode ? "glass-strong" : "bg-white border border-slate-200"
            }`}
            style={darkMode ? { background: "#0A1226" } : undefined}
          >
            <button
              onClick={() => !loading && onCancel?.()}
              className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                darkMode
                  ? "text-slate-500 hover:bg-white/[0.06] hover:text-slate-200"
                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              }`}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="px-6 pb-6 pt-7">
              <div
                className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl border ${
                  tone === "danger"
                    ? darkMode
                      ? "border-red-500/25 bg-red-500/12 text-red-300"
                      : "border-red-200 bg-red-50 text-red-600"
                    : darkMode
                    ? "border-accent-400/25 bg-accent-400/12 text-accent-200"
                    : "border-sky-200 bg-sky-50 text-sky-600"
                }`}
              >
                <AlertTriangle className="h-5 w-5" />
              </div>

              <h3 className={`text-base font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>
                {title}
              </h3>
              {message && (
                <p className={`mt-1.5 text-sm leading-relaxed ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                  {message}
                </p>
              )}

              <div className="mt-6 flex items-center justify-end gap-2.5">
                <button
                  onClick={() => !loading && onCancel?.()}
                  disabled={loading}
                  className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
                    darkMode
                      ? "border-white/10 text-slate-200 hover:bg-white/[0.06]"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {cancelLabel}
                </button>
                <button
                  onClick={() => onConfirm?.()}
                  disabled={loading}
                  className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all disabled:opacity-70 ${confirmClasses}`}
                >
                  {loading && (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current/40 border-t-current" />
                  )}
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ConfirmDialog;
